import { ChatCloseConfirm } from '@/components/chat/close-confirm';
import { Button } from '@/components/ui/button';
import { ApiError, closeChatRoom } from '@/lib/api';
import { chatRoomQueryKey } from '@/lib/chat-lobby';
import type { ChatRoomView } from '@matter_extractor/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

/**
 * « Terminer l'entretien » and its confirmation, as one control (chat spec §11).
 *
 * Whole rather than two halves a screen has to wire together, and that is the
 * point: the button, the modal of S5, the POST and the refusal live here, so
 * the conversation of S4 mounts ONE element and cannot mount the button without
 * the confirmation behind it. §11 makes that pairing the requirement - « ce
 * bouton met fin a une conversation que quelqu'un a accepte d'avoir » - and a
 * control that could be assembled wrong would eventually be.
 *
 * **It renders nothing at all for the guest.** The guest has no exit control
 * (decided 2026-09-02) and §3 asks for more than a hidden button: « le controle
 * de cloture n'existe dans le DOM de personne d'autre que l'interviewer ». So
 * this returns null on her role rather than styling something away - and the
 * server refuses her token in any case (`routes/chat-close.ts`), because a
 * screen is never the guard.
 */

export interface ChatCloseControlProps {
  room: ChatRoomView;
  /**
   * The opaque link token, passed IN rather than read from the router.
   *
   * It used to come from `useChatToken()`, which reaches for `useParams`. That
   * made this leaf drag a router into every tree that mounts it - and the tree
   * that mounts it is `conversation-panel.tsx`, which #9 deliberately kept
   * router-free so it could be rendered bare in a test. A hidden dependency in
   * a leaf is paid by every ancestor.
   */
  token: string;
}

/** The refusals this control can meet, worded from the STATUS and never from the server's sentence. */
function refusalWording(error: unknown): string {
  if (error instanceof ApiError) {
    // 403: this browser is holding the guest's link. It is not a thing the
    // interviewer can fix by clicking again, so it is worded as what it is.
    if (error.status === 403) return 'Ce lien n’est pas celui de l’interviewer.';
    // 410: the room went away between the screen and the gesture - and past the
    // grace hour of §10 the transcript is read through /admin/entretiens.
    if (error.status === 410) return 'Cette salle n’est plus joignable.';
    if (error.status === 429) return 'Trop de tentatives depuis cette adresse. Réessayez.';
  }
  return 'La clôture n’a pas abouti. Réessayez.';
}

export function ChatCloseControl({ room, token }: ChatCloseControlProps) {
  const queries = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const closing = useMutation({
    mutationFn: () => closeChatRoom(token),
    onSuccess: (closed) => {
      setConfirming(false);
      // The room is written straight into the cache rather than invalidated, so
      // the phase changes on the same frame: the interviewer clicked once and
      // must land on S7 rather than watch a spinner she has no reason to expect.
      //
      // The key comes from `chatRoomQueryKey` and is not spelled out here: the
      // shell of `chat.$token.tsx` polls under it (issue #8), and a second
      // spelling would write beside the cache the screen actually reads - which
      // fails by doing NOTHING, on the one gesture that has to land.
      queries.setQueryData(chatRoomQueryKey(token), closed);
    },
  });

  if (room.role !== 'interviewer') return null;
  // Already ended: the control has nothing left to do, and an « ended » room is
  // exactly where the read-only screen lives.
  if (room.closedAt !== null) return null;

  return (
    <>
      <Button
        type="button"
        variant="destructive"
        size="sm"
        data-testid="chat-close-button"
        onClick={() => {
          closing.reset();
          setConfirming(true);
        }}
      >
        Terminer l’entretien
      </Button>
      {confirming ? (
        <ChatCloseConfirm
          closing={closing.isPending}
          error={closing.isError ? refusalWording(closing.error) : null}
          onConfirm={() => closing.mutate()}
          onCancel={() => {
            closing.reset();
            setConfirming(false);
          }}
        />
      ) : null}
    </>
  );
}
